import { useState } from "react";
import {
  ImageOff,
  Lightbulb,
  RefreshCcw,
  Sprout
} from "lucide-react";

import type { Difficulty } from "@/domain";
import {
  type CurriculumAudioPlayer,
  useCurriculumFeedbackAudio
} from "@/features/audio";
import type { ActiveHintStage } from "@/game/feedback";
import { CurriculumVoiceButton } from "@/ui";

export interface ChineseDifficultyBadgeProps {
  readonly difficulty: Difficulty;
}

export function ChineseDifficultyBadge({
  difficulty
}: ChineseDifficultyBadgeProps) {
  return (
    <span
      aria-label={"第 " + String(difficulty) + " 阶难度"}
      className="chinese-player__difficulty"
    >
      <Sprout aria-hidden="true" />
      第 {difficulty} 阶
    </span>
  );
}

export interface ChineseFeedbackNoticeProps {
  readonly audioPlayer: CurriculumAudioPlayer;
  readonly enabled?: boolean;
  readonly feedback: {
    readonly audioPath: string;
    readonly kind: "hint" | "retry";
    readonly message: string;
    readonly playbackRequest: number;
    readonly stage: ActiveHintStage;
  } | null;
}

export function ChineseFeedbackNotice({
  audioPlayer,
  enabled = true,
  feedback
}: ChineseFeedbackNoticeProps) {
  const Icon = feedback?.kind === "retry" ? RefreshCcw : Lightbulb;
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    feedback,
    enabled
  );

  return (
    <div
      className="chinese-player__activity-feedback"
      data-feedback-kind={feedback?.kind ?? "none"}
      data-hint-stage={feedback?.stage ?? "none"}
    >
      {feedback === null ? (
        <span role="status">慢慢看，按自己的节奏来。</span>
      ) : (
        <>
          <span
            aria-atomic="true"
            aria-live="polite"
            className="chinese-player__activity-feedback-message"
            role="status"
          >
            <Icon aria-hidden="true" />
            <span>{feedback.message}</span>
          </span>
          <CurriculumVoiceButton
            isPlaying={feedbackAudio.isPlaying}
            label={feedback.message}
            onPlay={feedbackAudio.replay}
          />
        </>
      )}
    </div>
  );
}

export interface ManifestImageProps {
  readonly alt: string;
  readonly src: string;
}

export function ManifestImage({ alt, src }: ManifestImageProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc === src) {
    return (
      <span
        aria-label={alt}
        className="chinese-player__image-fallback"
        role="img"
      >
        <ImageOff aria-hidden="true" />
        <span>{alt}</span>
      </span>
    );
  }

  return (
    <img
      alt={alt}
      className="chinese-player__picture-image"
      draggable={false}
      onError={() => setFailedSrc(src)}
      src={src}
    />
  );
}
